import React, { useState } from 'react';
import PropTypes from 'prop-types';
import withApi from '../../components/higherOrderComponents/withApi';
import Modal from '../components/Modal';
import { useModal } from '../components/useModal';

/**
 * Maintenance — the two jobs the old "Developers" page runs.
 *
 * Both touch live data and neither asks anything there: the buttons run on
 * click. Cleaning old activities deletes rows; the booking check walks external
 * purchases and can act on what it finds. Both are confirmed here, and both say
 * what they will do before they do it.
 *
 * Whatever a job returns is shown as it comes back — these are diagnostics, and
 * summarising them would hide the one line somebody needed. The booking check is
 * the exception: it reports a success it has not checked, so the page says the job
 * started rather than repeating that claim.
 *
 * `/checks` is not ported: it calls `api.checks.bookings()` and there is no
 * `checks` module in `api/index.js`, so the page throws on mount. Its button is
 * named "Check external purchases", which is what the booking check below does.
 */

const MaintenanceInternal = ({ api }) => {
  const { ask, modalProps } = useModal();

  const [busy, setBusy] = useState(null);
  const [output, setOutput] = useState(null);
  const [note, setNote] = useState(null);

  const run = (key, fn, { unreported = false } = {}) => {
    setBusy(key);
    setOutput(null);
    setNote(null);
    return fn()
      .then(result => {
        if (unreported) {
          // `activities.runBookingCheck` fires the request without awaiting it
          // and returns `{ success: true }` unconditionally — its `catch` can
          // never see an HTTP failure. Printing that as the outcome would be
          // reporting a success nobody checked. Noted in REWRITE-STATE.md.
          setOutput('Started. This job answers before the work finishes and always\n'
            + 'reports success, so what it found is not visible here — read the\nserver logs for the result.');
          setNote({ tone: 'info', text: 'Started.' });
          return;
        }

        setOutput(result === undefined || result === null
          ? 'Finished, with nothing returned.'
          : (typeof result === 'string' ? result : JSON.stringify(result, null, 2)));
        setNote({ tone: 'info', text: 'Finished.' });
      })
      .catch(error => {
        setOutput(JSON.stringify(error?.response?.data ?? error?.message ?? error, null, 2));
        setNote({ tone: 'error', text: 'The job failed — its output is below.' });
        throw error;
      })
      .finally(() => setBusy(null));
  };

  const cleanActivities = () => ask({
    kind: 'danger',
    title: 'Clean old activities?',
    context: 'Maintenance · clean old activities',
    body: 'Activities whose date has passed are deleted. Slots nobody can book any more stop taking up space; bookings already made are not touched.',
    warning: 'It runs against live data and cannot be undone.',
    confirmLabel: 'Clean them',
    onConfirm: () => run('clean', () => api.activities.cleanOldActivities())
  });

  const bookingCheck = () => ask({
    kind: 'confirm',
    title: 'Run the booking check?',
    context: 'Maintenance · booking check',
    body: 'It walks external purchases looking for bookings that did not complete. The admin is told the job started, not what it found — that is in the server logs.',
    confirmLabel: 'Run it',
    onConfirm: () => run('check', () => api.activities.runBookingCheck(), { unreported: true })
  });

  return (
    <div className="next-page">
      <header className="next-page__header">
        <h1>Maintenance</h1>
        <p className="next-muted">Jobs that run against live data, on demand.</p>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <section className="next-card">
        <div className="next-card__head"><h2>Clean old activities</h2></div>
        <div className="next-cards__body">
          <p className="next-muted">
            Deletes activities whose date has passed. Bookings already made are untouched.
          </p>
          <button
            type="button"
            className="next-btn next-btn--danger-solid"
            disabled={!!busy}
            onClick={cleanActivities}
          >
            {busy === 'clean' ? 'Cleaning…' : 'Clean old activities'}
          </button>
        </div>
      </section>

      <section className="next-card">
        <div className="next-card__head"><h2>Booking check</h2></div>
        <div className="next-cards__body">
          <p className="next-muted">
            Walks external purchases for bookings that did not complete. Its result is
            not reported back to the admin — see the server logs.
          </p>
          <button
            type="button"
            className="next-btn next-btn--primary"
            disabled={!!busy}
            onClick={bookingCheck}
          >
            {busy === 'check' ? 'Running…' : 'Run the booking check'}
          </button>
        </div>
      </section>

      {output !== null && (
        <section className="next-card">
          <div className="next-card__head">
            <h2>Output</h2>
            <span className="next-card__spacer" />
            <button type="button" className="next-btn next-btn--sm" onClick={() => setOutput(null)}>
              Clear
            </button>
          </div>
          {/* As it came back: these are diagnostics, and a summary would hide the
              one line somebody needed. */}
          <pre className="next-pre">{output}</pre>
        </section>
      )}

      <Modal {...modalProps} />
    </div>
  );
};

MaintenanceInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Maintenance = withApi(MaintenanceInternal);

Maintenance.requiresLogin = true;

export default Maintenance;
